import { useEffect, useMemo, useState } from 'react';

import { ButtonLoadingAnimation, Icons, Text } from '@opendoor/bricks-next';
import { Box } from '@opendoor/bricks/core';
import debounce from 'lodash/debounce';

import {
  AddressValidationResult,
  validateAddress as validateAddressHelper,
} from '../../helpers/addressValidation';
import {
  ALLOWED_TYPES,
  buildAutocompleteUrl,
  buildPlacesUrl,
  generateGoogleMapsSessionToken,
} from '../../helpers/googleMaps';
import AutocompleteInput, { AutocompleteItem } from './AutocompleteInput';

interface PredictionTerm {
  offset: number;
  value: string;
}

export interface AutoCompletePrediction extends AutocompleteItem {
  description: string;
  place_id?: string;
  addressToken?: string;
  types?: string[];
  source: string;
  terms?: PredictionTerm[];
}

export type SelectedAddress = {
  street1: string;
  city: string;
  state: string;
  postal_code: string;
  unit?: string;
  latitude: number;
  longitude: number;
  id?: string;
  source?: 'google' | 'casa';
};

export type { AddressValidationResult };

export interface AddressInputProps {
  id: string;
  placeholder?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  label?: string;
  onAddressSelect: (address: SelectedAddress) => void;
  onAddressValidated?: (result: AddressValidationResult) => void;
  validateAddress?: boolean;
  onError?: (error: Error) => void;
  onPredictionsChange?: (predictions: AutoCompletePrediction[]) => void;
  onSearchTermChange?: (searchTerm: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  customAction?: React.ReactNode;
  listBoxId?: string;
  listBoxAriaLabel: string;
  icon?: React.ReactNode;
}

const AddressInput: React.FC<AddressInputProps> = ({
  id,
  placeholder,
  disabled,
  hideLabel,
  label,
  onAddressSelect,
  onAddressValidated,
  validateAddress = false,
  onError,
  onPredictionsChange,
  onSearchTermChange,
  onFocus,
  onBlur,
  customAction,
  listBoxId,
  listBoxAriaLabel,
  icon,
}) => {
  const [predictions, setPredictions] = useState<AutoCompletePrediction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionToken] = useState(() => generateGoogleMapsSessionToken());

  useEffect(() => {
    onPredictionsChange?.(predictions);
  }, [predictions, onPredictionsChange]);

  useEffect(() => {
    onSearchTermChange?.(searchTerm);
  }, [searchTerm, onSearchTermChange]);

  const fetchAutocompleteOptions = useMemo(
    () =>
      debounce(async (input: string) => {
        if (!input) {
          setPredictions([]);
          return;
        }

        try {
          const response = await fetch(buildAutocompleteUrl(input, sessionToken));
          const data = await response.json();

          // Clean descriptions by removing ', USA' and add required AutocompleteItem properties
          data.predictions.forEach((result: AutoCompletePrediction) => {
            result.description = result.description.replace(', USA', '');
            result.id = result.place_id || result.addressToken || result.description;
            result.ariaLabel = result.description;
          });

          // Filter predictions based on type and term count
          const filtered = data.predictions.filter((result: AutoCompletePrediction) => {
            result.source = data.source;
            if (data.source === 'google') {
              const terms = result.terms ?? [];
              const allowedTypes = (result.types ?? []).filter((type) =>
                ALLOWED_TYPES.includes(type),
              );
              return (
                (terms.length >= 5 && allowedTypes.length > 0) ||
                (terms.length === 4 && terms[0].value.match(/^\d+\S*\s\S+\s/) !== null)
              );
            }
            return true;
          });

          setPredictions(filtered);
        } catch (error) {
          setPredictions([]);
          onError?.(error as Error);
        }
      }, 100),
    [sessionToken, onError],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchAutocompleteOptions(value);
    if (value === '') {
      setPredictions([]);
    }
  };

  const handlePredictionSelect = async (prediction: AutoCompletePrediction) => {
    setSearchTerm(prediction.description);
    setPredictions([]);
    setIsLoading(true);

    const url =
      prediction.source === 'google'
        ? buildPlacesUrl({
            source: 'google',
            placeId: prediction.place_id,
            sessionToken,
          })
        : buildPlacesUrl({
            source: 'casa',
            addressToken: prediction.addressToken,
            sessionToken,
          });

    try {
      const response = await fetch(url);
      const address: SelectedAddress = await response.json();

      // Optionally validate the address
      if (validateAddress && onAddressValidated) {
        try {
          const validationResult = await validateAddressHelper(address);
          onAddressValidated(validationResult);
        } catch (validationError) {
          onError?.(validationError as Error);
        }
      }

      onAddressSelect(address);
    } catch (error) {
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderItem = (prediction: AutoCompletePrediction) => (
    <Text
      typography="$bodyXlarge"
      tag="span"
      textAlign="left"
      $smallerThanMD={{
        typography: '$bodyMedium',
      }}
      px={10}
      py={2}
    >
      {prediction.description}
    </Text>
  );

  const displayIcon =
    icon ??
    (isLoading ? (
      <Box position="relative" px={5}>
        <ButtonLoadingAnimation variant="secondary" size="xsmall" />
      </Box>
    ) : (
      <Icons.Search />
    ));

  return (
    <AutocompleteInput<AutoCompletePrediction>
      id={id}
      label={label}
      hideLabel={hideLabel}
      placeholder={placeholder}
      disabled={disabled}
      items={predictions}
      searchTerm={searchTerm}
      onSearchChange={handleInputChange}
      onItemSelect={handlePredictionSelect}
      renderItem={renderItem}
      suffix={displayIcon}
      customAction={customAction}
      listBoxId={listBoxId}
      onEscape={() => setPredictions([])}
      onFocus={onFocus}
      onBlur={onBlur}
      listBoxAriaLabel={listBoxAriaLabel}
    />
  );
};

export default AddressInput;
